import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DoctorLayout from '../components/DoctorLayout';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import { appointmentAPI, patientAPI, insuranceAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useWebSocketContext } from '../hooks/useWebSocketContext';
import styles from './AppointmentsPage.module.css';

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workingHours, setWorkingHours] = useState([]);
  useEffect(() => {
    appointmentAPI.getWorkingHours().then(data => setWorkingHours(data.availabilities || [])).catch(() => setError('No pudimos cargar tus horarios de atención.'));
  }, []);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    serviceId: '',
    appointment_date: '',
    appointment_time: '',
    reason_for_visit: '',
    insurance_company_id: ''
  });
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [patientInsurances, setPatientInsurances] = useState([]);
  const { isConnected } = useWebSocketContext();
  const [delayModal, setDelayModal] = useState({ show: false, appointmentId: null });
  const [delayMinutes, setDelayMinutes] = useState(15);

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'day');

  // Estados para Alta Rápida de Paciente dentro del modal de Turno
  const [showQuickPatientForm, setShowQuickPatientForm] = useState(false);
  const [quickPatientData, setQuickPatientData] = useState({
    name: '',
    phone: '',
    email: '',
    document_number: ''
  });
  const [savingQuickPatient, setSavingQuickPatient] = useState(false);
  const [quickPatientError, setQuickPatientError] = useState('');
  const [quickPatientSuccess, setQuickPatientSuccess] = useState('');

  // New calendar states
  const [viewMode, setViewMode] = useState(() => (searchParams.get('tab') || 'day') === 'day' ? 'agenda' : 'list'); // 'list' or 'agenda'
  const [expandedApptId, setExpandedApptId] = useState(null);

  const toggleExpandAppt = (apptId) => {
    setExpandedApptId(prev => prev === apptId ? null : apptId);
  };
  
  const getLocalDateString = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (dateStr) => {
    const parts = dateStr.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  const getMondayOfDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    return getLocalDateString(monday);
  };

  const [selectedDate, setSelectedDate] = useState(() => getLocalDateString(new Date()));
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMondayOfDate(getLocalDateString(new Date())));

  const getDaysOfWeek = (mondayStr) => {
    const days = [];
    const monday = parseLocalDate(mondayStr);
    const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      days.push({
        name: dayNames[i],
        dateStr: getLocalDateString(dayDate),
        dayNum: dayDate.getDate()
      });
    }
    return days;
  };

  const generateTimeSlots = (dayAppointments = []) => {
    const slots = new Set();
    
    const day = parseLocalDate(selectedDate).getDay();
    workingHours.filter(shift => Number(shift.day_of_week) === day).forEach(shift => {
      const toMinutes = time => Number(time.slice(0,2))*60 + Number(time.slice(3,5));
      for (let minute = toMinutes(shift.start_time); minute < toMinutes(shift.end_time); minute += 30) {
        slots.add(String(Math.floor(minute/60)).padStart(2,'0') + ':' + String(minute%60).padStart(2,'0'));
      }
    });

    // Dynamically insert any scheduled appointment times for the selected date
    dayAppointments.forEach(appt => {
      if (appt.appointment_date && appt.appointment_time) {
        const dateStr = String(appt.appointment_date).split('T')[0];
        if (dateStr === selectedDate) {
          const apptTime = String(appt.appointment_time).substring(0, 5);
          slots.add(apptTime);
        }
      }
    });
    
    return Array.from(slots).sort();
  };

  const handlePrevWeek = () => {
    const monday = parseLocalDate(currentWeekStart);
    monday.setDate(monday.getDate() - 7);
    const newMondayStr = getLocalDateString(monday);
    setCurrentWeekStart(newMondayStr);
    setSelectedDate(newMondayStr);
  };

  const handleNextWeek = () => {
    const monday = parseLocalDate(currentWeekStart);
    monday.setDate(monday.getDate() + 7);
    const newMondayStr = getLocalDateString(monday);
    setCurrentWeekStart(newMondayStr);
    setSelectedDate(newMondayStr);
  };

  const handleGoToToday = () => {
    const todayStr = getLocalDateString(new Date());
    setSelectedDate(todayStr);
    setCurrentWeekStart(getMondayOfDate(todayStr));
  };

  const getWeekRangeLabel = () => {
    const monday = parseLocalDate(currentWeekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const options = { day: 'numeric', month: 'short' };
    const startStr = monday.toLocaleDateString('es-ES', options);
    const endStr = sunday.toLocaleDateString('es-ES', options);
    const yearStr = monday.getFullYear();
    
    return `${startStr} - ${endStr}, ${yearStr}`;
  };

  const handleEmptySlotClick = (timeSlot) => {
    setFormData({
      patientId: '',
      serviceId: '',
      appointment_date: selectedDate,
      appointment_time: timeSlot,
      reason_for_visit: '',
      insurance_company_id: ''
    });
    setSelectedService(null);
    setPatientInsurances([]);
    setAvailableSlots(prev => prev.includes(timeSlot) ? prev : [...prev, timeSlot].sort());
    setShowForm(true);
  };

  useEffect(() => {
    fetchAppointments();
    fetchPatients();
    fetchServices();
  }, []);

  // Cargar slots disponibles cuando cambia la fecha o el servicio
  useEffect(() => {
    if (formData.appointment_date) {
      const duration = selectedService ? selectedService.duration_minutes : 30;
      loadAvailableSlots(formData.appointment_date, duration);
    } else {
      setAvailableSlots([]);
    }
  }, [formData.appointment_date, selectedService]);

  // Cargar obras sociales del paciente
  useEffect(() => {
    if (formData.patientId) {
      loadPatientInsurances(formData.patientId);
    } else {
      setPatientInsurances([]);
    }
  }, [formData.patientId]);

  const loadPatientInsurances = async (patientId) => {
    try {
      const response = await insuranceAPI.getPatientInsurances(patientId);
      if (response.success) {
        setPatientInsurances(response.insurances);
      }
    } catch (err) {
      console.error('Error cargando obras sociales del paciente:', err);
      setPatientInsurances([]);
    }
  };

  const fetchServices = async () => {
    try {
      // Usaremos el endpoint que creamos antes para obtener servicios propios
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002'}/api/services/doctor/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setServices(response.data.services);
      }
    } catch (err) {
      console.error('Error cargando servicios:', err);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await patientAPI.getPatients();
      if (response.success) {
        setPatients(response.patients);
      }
    } catch (err) {
      console.error('Error cargando pacientes:', err);
    }
  };

  const loadAvailableSlots = async (date, duration = 30) => {
    try {
      setLoadingSlots(true);
      const response = await appointmentAPI.getPublicAvailableSlots(user?.id || '', date, duration);
      if (response.success) {
        setAvailableSlots(response.slots || []);
      }
    } catch (err) {
      console.error('Error cargando horarios disponibles:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    if (tabName === 'day') {
      setViewMode('agenda');
    } else {
      setViewMode('list');
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabName);
    setSearchParams(newParams);
  };

  useEffect(() => {
    let filtered = [...appointments];

    if (activeTab === 'day') {
      filtered = filtered.filter(a => {
        const dateStr = String(a.appointment_date).split('T')[0];
        return dateStr === selectedDate;
      });
    } else if (activeTab === 'week') {
      const monday = parseLocalDate(currentWeekStart);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const monStr = getLocalDateString(monday);
      const sunStr = getLocalDateString(sunday);
      
      filtered = filtered.filter(a => {
        const dateStr = String(a.appointment_date).split('T')[0];
        return dateStr >= monStr && dateStr <= sunStr;
      });
    } else if (activeTab === 'pending') {
      filtered = filtered.filter(a => ['pending', 'pending_payment'].includes(a.status));
    } else if (activeTab === 'cancelled') {
      filtered = filtered.filter(a => ['cancelled', 'rejected', 'expired'].includes(a.status));
    } else { // activeTab === 'all'
      if (filterStatus !== 'all') {
        filtered = filtered.filter(a => a.status === filterStatus);
      }
      if (filterDate) {
        filtered = filtered.filter(a => {
          const dateStr = String(a.appointment_date).split('T')[0];
          return dateStr === filterDate;
        });
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => {
        const name = (a.patient_name || '').toLowerCase();
        const phone = (a.patient_phone || '').toLowerCase();
        return name.includes(term) || phone.includes(term);
      });
    }

    setFilteredAppointments(filtered);
  }, [appointments, searchTerm, filterStatus, filterDate, activeTab, selectedDate, currentWeekStart]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentAPI.getAppointments();

      if (response.success) {
        const sorted = response.appointments.sort((a, b) => {
          const dateAStr = String(a.appointment_date).split('T')[0];
          const dateBStr = String(b.appointment_date).split('T')[0];
          const dateA = new Date(`${dateAStr}T${a.appointment_time}`);
          const dateB = new Date(`${dateBStr}T${b.appointment_time}`);
          return dateB - dateA;
        });
        setAppointments(sorted);
      }

      setError(null);
    } catch (err) {
      console.error('Error cargando citas:', err);
      setError('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await appointmentAPI.updateAppointment(appointmentId, {
        status: newStatus
      });

      if (response.success) {
        setAppointments(prev =>
          prev.map(a => a.id === appointmentId ? response.appointment : a)
        );
      }
    } catch (err) {
      console.error('Error actualizando cita:', err);
      alert('Error al actualizar la cita');
    }
  };

  const handleDelay = async () => {
    if (!delayMinutes || delayMinutes <= 0) {
      alert('Por favor ingresa minutos válidos');
      return;
    }

    try {
      const response = await appointmentAPI.updateDelay(delayModal.appointmentId, {
        delay_minutes: delayMinutes,
        delay_reason: 'Retraso registrado'
      });

      if (response.success) {
        setAppointments(prev =>
          prev.map(a => a.id === delayModal.appointmentId
            ? { ...a, delay_minutes: delayMinutes }
            : a)
        );
        alert(`✓ Retraso de ${delayMinutes} minutos registrado`);
        setDelayModal({ show: false, appointmentId: null });
        setDelayMinutes(15);
      }
    } catch (err) {
      console.error('Error registrando retraso:', err);
      alert('Error al registrar el retraso');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'serviceId') {
      const service = services.find(s => s.id === parseInt(value));
      setSelectedService(service);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();

    if (!formData.patientId || !formData.appointment_date || !formData.appointment_time) {
      alert('Por favor completa los campos requeridos');
      return;
    }

    try {
      setFormSubmitting(true);
      const response = await appointmentAPI.createAppointment(formData);

      if (response.success) {
        const newAppt = response.appointment;
        setAppointments(prev => [newAppt, ...prev]);
        resetForm();
        setShowForm(false);

        const phone = newAppt?.patient_phone || newAppt?.phone;
        if (phone) {
          const confirmSend = window.confirm('✓ Turno agendado correctamente.\n\n¿Deseas notificar la confirmación con todos los detalles al paciente por WhatsApp?');
          if (confirmSend) {
            handleWhatsAppConfirmation(newAppt);
          }
        } else {
          alert('✓ Turno agendado correctamente');
        }
      }
    } catch (err) {
      console.error('Error creando cita:', err);
      alert(err.response?.data?.message || 'Error al crear la cita');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleSaveQuickPatient = async (e) => {
    if (e) e.preventDefault();
    if (!quickPatientData.name || !quickPatientData.name.trim()) {
      setQuickPatientError('El nombre del paciente es obligatorio.');
      return;
    }
    setSavingQuickPatient(true);
    setQuickPatientError('');
    setQuickPatientSuccess('');
    try {
      const response = await patientAPI.createPatient(quickPatientData);
      if (response.success && response.patient) {
        const newPatient = response.patient;
        setPatients(prev => [newPatient, ...prev]);
        setFormData(prev => ({
          ...prev,
          patientId: newPatient.id
        }));
        setQuickPatientSuccess(`✓ Paciente "${newPatient.name}" registrado y seleccionado automáticamente`);
        setQuickPatientData({ name: '', phone: '', email: '', document_number: '' });
        setShowQuickPatientForm(false);
        setTimeout(() => setQuickPatientSuccess(''), 4000);
      }
    } catch (err) {
      console.error('Error al crear paciente rápido:', err);
      setQuickPatientError(err.response?.data?.message || 'Error al registrar el paciente.');
    } finally {
      setSavingQuickPatient(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      serviceId: '',
      appointment_date: '',
      appointment_time: '',
      reason_for_visit: '',
      insurance_company_id: ''
    });
    setSelectedService(null);
    setPatientInsurances([]);
    setShowQuickPatientForm(false);
    setQuickPatientData({ name: '', phone: '', email: '', document_number: '' });
    setQuickPatientError('');
    setQuickPatientSuccess('');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Por Aprobar', class: styles.statusPending },
      pending_payment: { label: 'Pendiente de Pago', class: styles.statusPending },
      scheduled: { label: 'Programado', class: styles.statusScheduled },
      completed: { label: 'Completado', class: styles.statusCompleted },
      cancelled: { label: 'Cancelado', class: styles.statusCancelled },
      rejected: { label: 'Rechazado', class: styles.statusRejected },
      absent: { label: 'Ausente', class: styles.statusAbsent },
      expired: { label: 'Solicitud Vencida', class: styles.statusExpired }
    };
    const badge = badges[status] || badges.scheduled;
    return <span className={`${styles.statusBadge} ${badge.class}`}>{badge.label}</span>;
  };

  const handleAcceptAppointment = async (appointmentId) => {
    try {
      setLoading(true);
      const response = await appointmentAPI.acceptAppointment(appointmentId);
      if (response.success) {
        const targetAppt = appointments.find(a => a.id === appointmentId);
        const updated = appointments.map(a =>
          a.id === appointmentId ? { ...a, status: 'scheduled' } : a
        );
        setAppointments(updated);

        const phone = targetAppt?.patient_phone || targetAppt?.phone;
        if (targetAppt && phone) {
          const confirmSend = window.confirm('✓ Turno confirmado con éxito.\n\n¿Deseas enviar la confirmación del turno al paciente por WhatsApp ahora mismo?');
          if (confirmSend) {
            handleWhatsAppConfirmation({ ...targetAppt, status: 'scheduled' });
          }
        } else {
          alert('✓ Cita aceptada correctamente');
        }
      }
    } catch (err) {
      console.error('Error aceptando cita:', err);
      alert('Error al aceptar la cita');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectAppointment = async (appointmentId) => {
    const reason = window.prompt('¿Por qué rechazas esta cita?');
    if (reason === null) return; // Cancelado

    try {
      setLoading(true);
      const response = await appointmentAPI.rejectAppointment(appointmentId, reason);
      if (response.success) {
        const updated = appointments.map(a =>
          a.id === appointmentId ? { ...a, status: 'rejected' } : a
        );
        setAppointments(updated);
        alert('✓ Cita rechazada');
      }
    } catch (err) {
      console.error('Error rechazando cita:', err);
      alert('Error al rechazar la cita');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppConfirmation = (appt) => {
    const phone = appt.patient_phone || appt.phone;
    if (!phone) {
      alert('Este paciente no posee número de teléfono registrado');
      return;
    }

    const cleanPhone = String(phone).replace(/\D/g, '');
    const dateStr = String(appt.appointment_date).split('T')[0];
    const d = new Date(dateStr + 'T12:00:00');
    const formattedDate = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    const timeStr = String(appt.appointment_time || '').substring(0, 5);

    let message = `*¡Hola ${appt.patient_name || 'Paciente'}!* 👋\n\n`;
    message += `Tu turno con *${user?.name || 'el profesional'}* ha sido *CONFIRMADO* ✅\n\n`;
    message += `📅 *Fecha:* ${formattedDate}\n`;
    message += `⏰ *Hora:* ${timeStr} hs\n`;

    if (appt.service_name) {
      message += `📋 *Servicio:* ${appt.service_name}\n`;
    }

    if (appt.meet_link) {
      message += `📹 *Modalidad:* Online (Videollamada)\n🔗 *Link de ingreso:* ${appt.meet_link}\n`;
    } else if (user?.clinic_address) {
      message += `📍 *Lugar:* ${user.clinic_address}\n`;
    }

    message += `\n¡Te esperamos! Por cualquier consulta puedes responder a este mensaje.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppReminder = (appt) => {
    handleWhatsAppConfirmation(appt);
  };

  const renderMobileAgendaList = (slots) => {
    return (
      <div className={styles.mobileAgendaList}>
        {slots.map((slot) => {
          const slotAppointments = filteredAppointments.filter((appt) => {
            const dateStr = String(appt.appointment_date).split('T')[0];
            if (dateStr !== selectedDate) return false;
            const apptTime = String(appt.appointment_time).substring(0, 5);
            return apptTime === slot;
          });

          if (slotAppointments.length === 0) {
            return (
              <div key={slot} className={styles.mobileAgendaEmptySlot} onClick={() => handleEmptySlotClick(slot)}>
                <div className={styles.mobileAgendaTime}>{slot}</div>
                <div className={styles.mobileAgendaAddArea}>
                  <span className={styles.plusIcon}>+</span>
                  <span>Disponible (Crear cita)</span>
                </div>
              </div>
            );
          }

          return slotAppointments.map((appt) => {
            return (
              <div key={`${slot}-${appt.id}`} className={styles.mobileAgendaSlotCard}>
                <div className={styles.mobileAgendaCardHeader}>
                  <div className={styles.mobileAgendaTimeActive}>{slot} hs</div>
                  <div className={styles.mobileAgendaBadgeGroup}>
                    {getStatusBadge(appt.status)}
                    {appt.delay_minutes > 0 && (
                      <span className={styles.mobileDelayLabel}>+{appt.delay_minutes}m</span>
                    )}
                  </div>
                </div>
                
                <div className={styles.mobileAgendaCardBody}>
                  <h4 className={styles.mobilePatientName}>{appt.patient_name}</h4>
                  <p className={styles.mobilePatientPhone}>📞 {appt.patient_phone || 'Sin teléfono'}</p>
                  <p className={styles.mobileAgendaServiceText}>💼 {appt.service_name || 'Consulta General'}</p>
                  {appt.reason_for_visit && (
                    <p className={styles.mobileAgendaReasonText}>
                      <strong>Motivo:</strong> {appt.reason_for_visit}
                    </p>
                  )}
                </div>

                <div className={styles.mobileAgendaActions}>
                  {['pending', 'pending_payment'].includes(appt.status) ? (
                    <div className={styles.cardActionsMobile}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAcceptAppointment(appt.id); }}
                        className={styles.mobileAcceptBtn}
                      >
                        Aceptar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRejectAppointment(appt.id); }}
                        className={styles.mobileRejectBtn}
                      >
                        Rechazar
                      </button>
                    </div>
                  ) : ['scheduled', 'absent'].includes(appt.status) ? (
                    <div className={styles.cardActionsMobile}>
                      <select
                        value={appt.status}
                        onChange={(e) => { e.stopPropagation(); handleStatusChange(appt.id, e.target.value); }}
                        className={styles.mobileStatusSelect}
                      >
                        <option value="scheduled">Programado</option>
                        <option value="completed">Completado</option>
                        <option value="cancelled">Cancelado</option>
                        <option value="absent">Ausente</option>
                      </select>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDelayModal({ show: true, appointmentId: appt.id }); }}
                        className={styles.mobileDelayBtn}
                      >
                        ⏱️ Retrasar
                      </button>
                    </div>
                  ) : null}
                  {!['cancelled', 'rejected'].includes(appt.status) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleWhatsAppReminder(appt); }}
                      className={styles.mobileWhatsappBtn}
                    >
                      <Icon name="whatsapp" size={16} color="#FFF" />
                      <span>Recordar</span>
                    </button>
                  )}
                </div>
              </div>
            );
          });
        })}
      </div>
    );
  };

  const renderAgendaGrid = () => {
    const slots = generateTimeSlots(appointments);
    
    return (
      <div className={styles.agendaContainer}>
        <div className={styles.mobileAgendaViewOnly}>
          {renderMobileAgendaList(slots)}
        </div>
        <div className={styles.desktopAgendaViewOnly}>
          <div className={styles.tableContainer}>
            <table className={styles.agendaTable}>
              <thead>
                <tr>
                  <th className={styles.timeColHeader}>Hora</th>
                  <th className={styles.statusColHeader}>Presentismo</th>
                  <th className={styles.patientColHeader}>Paciente</th>
                  <th className={styles.insuranceColHeader}>Obra social / Plan</th>
                  <th className={styles.observationColHeader}>Observación</th>
                  <th className={styles.actionsColHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => {
                  const slotAppointments = filteredAppointments.filter((appt) => {
                    const dateStr = String(appt.appointment_date).split('T')[0];
                    if (dateStr !== selectedDate) return false;
                    const apptTime = String(appt.appointment_time).substring(0, 5);
                    return apptTime === slot;
                  });

                  if (slotAppointments.length === 0) {
                    return (
                      <tr key={slot} className={styles.emptyRow}>
                        <td className={styles.timeCell}>
                          <span className={styles.timeLabel}>{slot}</span>
                        </td>
                        <td colSpan="5" className={styles.emptySlotCell}>
                          <button 
                            type="button"
                            onClick={() => handleEmptySlotClick(slot)}
                            className={styles.availableSlotBtn}
                            title="Hacer clic para crear una cita en este horario"
                          >
                            <span className={styles.plusIcon}>+</span>
                            <span className={styles.availableText}>Disponible</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return slotAppointments.map((appt, index) => {
                    const isExpanded = expandedApptId === appt.id;
                    return (
                      <tr 
                        key={`${slot}-${appt.id}`} 
                        className={`${styles.agendaRow} ${isExpanded ? styles.cardExpanded : ''}`}
                        onClick={() => toggleExpandAppt(appt.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {index === 0 ? (
                          <td className={styles.timeCell} rowSpan={slotAppointments.length}>
                            <span className={styles.timeLabel}>{slot}</span>
                          </td>
                        ) : null}
                        <td data-label="Presentismo" className={styles.statusCell}>
                          {getStatusBadge(appt.status)}
                          {appt.delay_minutes > 0 && (
                            <div className={styles.delayBadge}>+{appt.delay_minutes} min</div>
                          )}
                          <div className={`${styles.mobileChevron} ${isExpanded ? styles.rotated : ''}`}>
                            <Icon name="chevronRight" size={16} />
                          </div>
                        </td>
                        <td data-label="Paciente" className={styles.patientCell}>
                          <div className={styles.pName}>{appt.patient_name}</div>
                          <div className={styles.pPhone}>{appt.patient_phone}</div>
                        </td>
                        <td data-label="Obra social / Plan" className={styles.insuranceCell}>
                          {appt.insurance_name ? (
                            <div className={styles.insuranceBadge}>
                              {appt.insurance_name}
                              {appt.insurance_plan_name ? ` (${appt.insurance_plan_name})` : ''}
                            </div>
                          ) : (
                            <span className={styles.particularLabel}>Particular</span>
                          )}
                        </td>
                        <td data-label="Observación" className={styles.observationCell}>
                          <span className={styles.serviceNameBadge}>
                            {appt.service_name || 'Consulta General'}
                          </span>
                          <div className={styles.reasonText}>
                            {appt.reason_for_visit || 'Visita general'}
                          </div>
                        </td>
                        <td data-label="Acciones" className={styles.actionsCell}>
                          <div className={styles.actionButtons}>
                            {['pending', 'pending_payment'].includes(appt.status) ? (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleAcceptAppointment(appt.id); }}
                                  className={styles.acceptBtn}
                                  title="Confirmar/Aceptar cita"
                                >
                                  ✓ Aceptar
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleRejectAppointment(appt.id); }}
                                  className={styles.rejectBtn}
                                  title="Rechazar cita"
                                >
                                  ✗ Rechazar
                                </button>
                              </>
                            ) : ['scheduled', 'absent'].includes(appt.status) ? (
                              <>
                                <select
                                  value={appt.status}
                                  onChange={(e) => { e.stopPropagation(); handleStatusChange(appt.id, e.target.value); }}
                                  className={styles.statusSelect}
                                >
                                  <option value="scheduled">Programado</option>
                                  <option value="completed">Completado</option>
                                  <option value="cancelled">Cancelado</option>
                                  <option value="absent">Ausente</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setDelayModal({ show: true, appointmentId: appt.id }); }}
                                  className={styles.delayBtn}
                                  title="Registrar retraso"
                                >
                                  ⏱️ Retraso
                                </button>
                              </>
                            ) : (
                              <div className={styles.statusOnly}>
                                {getStatusBadge(appt.status)}
                              </div>
                            )}
                            
                            {!['cancelled', 'rejected'].includes(appt.status) && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleWhatsAppReminder(appt); }}
                                className={styles.whatsappBtn}
                                title="Recordar cita por WhatsApp"
                              >
                                <Icon name="whatsapp" size={18} color="#FFF" />
                                WPP
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const days = getDaysOfWeek(currentWeekStart);
    return (
      <div className={styles.weekViewContainer}>
        {/* Navegador de Semana */}
        <div className={styles.weekNavigator}>
          <button type="button" onClick={handlePrevWeek} className={styles.navBtn}>
            <Icon name="chevronLeft" size={18} />
          </button>
          <button type="button" onClick={handleGoToToday} className={styles.todayBtn}>
            Hoy
          </button>
          <button type="button" onClick={handleNextWeek} className={styles.navBtn}>
            <Icon name="chevronRight" size={18} />
          </button>
          <span className={styles.weekLabel}>
            {getWeekRangeLabel()}
          </span>
        </div>

        <div className={styles.weekDaysGrid}>
          {days.map((day) => {
            const dayAppointments = appointments.filter(a => {
              const dateStr = String(a.appointment_date).split('T')[0];
              return dateStr === day.dateStr;
            });
            const isToday = day.dateStr === getLocalDateString(new Date());

            return (
              <div 
                key={day.dateStr} 
                className={`${styles.weekDayCard} ${isToday ? styles.todayWeekCard : ''}`}
              >
                <div className={styles.weekDayHeader}>
                  <div>
                    <span className={styles.weekDayName}>{day.name}</span>
                    <strong className={styles.weekDayNum}>{day.dayNum}</strong>
                  </div>
                  <span className={styles.weekApptCount}>{dayAppointments.length} turnos</span>
                </div>
                
                <div className={styles.weekDayBody}>
                  {dayAppointments.length === 0 ? (
                    <p className={styles.noApptsText}>Sin turnos</p>
                  ) : (
                    dayAppointments.sort((a,b) => a.appointment_time.localeCompare(b.appointment_time)).map(appt => (
                      <div 
                        key={appt.id} 
                        className={`${styles.weekApptItem} ${styles['status_' + appt.status] || ''}`}
                        onClick={() => toggleExpandAppt(appt.id)}
                      >
                        <span className={styles.weekApptTime}>{appt.appointment_time.substring(0, 5)} hs</span>
                        <span className={styles.weekApptName}>{appt.patient_name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DoctorLayout>
        <Loading />
      </DoctorLayout>
    );
  }

  return (
    <DoctorLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Gestión de Turnos</h1>
            <p className={styles.subtitle}>Agenda, aprueba y haz seguimiento de tus citas médicas</p>
          </div>
          <button onClick={() => setShowForm(true)} className={styles.addBtn}>
            <Icon name="plus" size={18} color="#FFF" />
            Nuevo Turno
          </button>
        </div>

        {error && <div className={styles.errorBox}>{error}</div>}

        {/* Modal de crear cita */}
        {showForm && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h2>Nuevo Turno</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className={styles.closeBtn}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreateAppointment} className={styles.form}>
                {quickPatientSuccess && (
                  <div className={styles.quickPatientSuccessMsg}>
                    {quickPatientSuccess}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <div className={styles.patientLabelRow}>
                    <label style={{ margin: 0 }}>Paciente / Cliente*</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowQuickPatientForm(!showQuickPatientForm);
                        setQuickPatientError('');
                      }}
                      className={styles.quickPatientToggleBtn}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>person_add</span>
                      {showQuickPatientForm ? 'Cerrar Alta Rápida' : '+ Crear Paciente Nuevo'}
                    </button>
                  </div>

                  {showQuickPatientForm && (
                    <div className={styles.quickPatientBox}>
                      <div className={styles.quickPatientBoxHeader}>
                        <h4>➕ Alta Rápida de Nuevo Paciente</h4>
                        <span className={styles.quickPatientSub}>Ingresa los datos y se asignará automáticamente a este turno sin salir de la pantalla.</span>
                      </div>
                      <div className={styles.quickPatientGrid}>
                        <div className={styles.formGroup}>
                          <label>Nombre y Apellido*</label>
                          <input
                            type="text"
                            placeholder="Ej: Juan Pérez"
                            value={quickPatientData.name}
                            onChange={(e) => setQuickPatientData(prev => ({ ...prev, name: e.target.value }))}
                            required
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>DNI / Documento</label>
                          <input
                            type="text"
                            placeholder="Ej: 38123456"
                            value={quickPatientData.document_number}
                            onChange={(e) => setQuickPatientData(prev => ({ ...prev, document_number: e.target.value }))}
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Teléfono / WhatsApp</label>
                          <input
                            type="tel"
                            placeholder="Ej: +54 9 11 1234-5678"
                            value={quickPatientData.phone}
                            onChange={(e) => setQuickPatientData(prev => ({ ...prev, phone: e.target.value }))}
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Email (Opcional)</label>
                          <input
                            type="email"
                            placeholder="paciente@email.com"
                            value={quickPatientData.email}
                            onChange={(e) => setQuickPatientData(prev => ({ ...prev, email: e.target.value }))}
                          />
                        </div>
                      </div>

                      {quickPatientError && <p className={styles.quickErrorMsg}>{quickPatientError}</p>}

                      <div className={styles.quickPatientActions}>
                        <button
                          type="button"
                          onClick={handleSaveQuickPatient}
                          disabled={savingQuickPatient}
                          className={styles.saveQuickBtn}
                        >
                          {savingQuickPatient ? 'Guardando...' : '✓ Guardar Paciente y Asignar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowQuickPatientForm(false)}
                          className={styles.cancelQuickBtn}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  <select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Selecciona un paciente</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} ({patient.phone || 'Sin teléfono'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Servicio / Tratamiento*</label>
                  <select
                    name="serviceId"
                    value={formData.serviceId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Selecciona un servicio</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} (${parseFloat(service.price).toLocaleString()} - {service.duration_minutes} min)
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Fecha*</label>
                    <input
                      type="date"
                      name="appointment_date"
                      value={formData.appointment_date}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Hora*</label>
                    {formData.appointment_date ? (
                      loadingSlots ? (
                        <div className={styles.loadingSlots}>Cargando horarios disponibles...</div>
                      ) : availableSlots.length > 0 ? (
                        <select
                          name="appointment_time"
                          value={formData.appointment_time}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Selecciona un horario</option>
                          {availableSlots.map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={styles.noSlots}>
                          No hay horarios disponibles para esta fecha
                        </div>
                      )
                    ) : (
                      <input
                        type="text"
                        placeholder="Primero selecciona fecha"
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Motivo del turno</label>
                  <input
                    type="text"
                    name="reason_for_visit"
                    placeholder="Ej: Revisión general, Consulta de control..."
                    value={formData.reason_for_visit}
                    onChange={handleFormChange}
                  />
                </div>

                {patientInsurances.length > 0 && (
                  <div className={styles.formGroup}>
                    <label>Obra Social</label>
                    <select
                      name="insurance_company_id"
                      value={formData.insurance_company_id}
                      onChange={handleFormChange}
                    >
                      <option value="">Sin obra social</option>
                      {patientInsurances.map(insurance => (
                        <option key={insurance.id} value={insurance.id}>
                          {insurance.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={styles.formActions}>
                  <button type="submit" className={styles.submitBtn} disabled={formSubmitting}>
                    {formSubmitting ? 'Creando...' : 'Crear Turno'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className={styles.cancelBtn}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pestañas de Navegación Unificada */}
        <div className={styles.tabsHeader}>
          <button 
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'day' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('day')}
          >
            <Icon name="clock" size={16} /> Día / Agenda
          </button>
          <button 
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'week' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('week')}
          >
            <Icon name="calendar" size={16} /> Semana
          </button>
          <button 
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'all' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('all')}
          >
            <Icon name="list" size={16} /> Lista Completa
          </button>
          <button 
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'pending' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            <Icon name="warning" size={16} /> Pendientes
          </button>
          <button 
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'cancelled' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            <Icon name="x" size={16} /> Cancelados
          </button>
        </div>

        {/* Toolbar de filtros (Buscador general siempre visible) */}
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Icon name="search" size={18} color="#64748b" />
            <input
              type="text"
              placeholder="Buscar cliente o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filters}>
            {activeTab === 'all' && (
              <>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className={styles.select}
                >
                  <option value="all">Todos los estados</option>
                  <option value="pending">Pendiente de Aprobación</option>
                  <option value="pending_payment">Pendiente de Pago</option>
                  <option value="scheduled">Programado</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                  <option value="rejected">Rechazado</option>
                  <option value="absent">Ausente / No asistió</option>
                  <option value="expired">Solicitud Vencida / No Respondida</option>
                </select>

                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className={styles.select}
                />
              </>
            )}

            <button onClick={fetchAppointments} className={styles.refreshBtn} title="Actualizar">
              <Icon name="refresh" size={18} color="currentColor" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Navigation & Day Selector for Agenda */}
        {activeTab === 'day' && (
          <div className={styles.calendarNavContainer}>
            <div className={styles.calendarNavControls}>
              <button type="button" onClick={handlePrevWeek} className={styles.navBtn} title="Semana anterior">
                <Icon name="chevronLeft" size={18} />
              </button>
              <button type="button" onClick={handleGoToToday} className={styles.todayBtn}>
                Hoy
              </button>
              <button type="button" onClick={handleNextWeek} className={styles.navBtn} title="Semana siguiente">
                <Icon name="chevronRight" size={18} />
              </button>
              <span className={styles.weekLabel}>
                {getWeekRangeLabel()}
              </span>
            </div>
            
            <div className={styles.dayPills}>
              {getDaysOfWeek(currentWeekStart).map((day) => {
                const isSelected = day.dateStr === selectedDate;
                const isToday = day.dateStr === getLocalDateString(new Date());
                return (
                  <button
                    key={day.dateStr}
                    type="button"
                    onClick={() => setSelectedDate(day.dateStr)}
                    className={`${styles.dayPill} ${isSelected ? styles.activePill : ''} ${isToday ? styles.todayPill : ''}`}
                  >
                    <span className={styles.dayName}>{day.name}</span>
                    <span className={styles.dayNum}>{day.dayNum}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Contenido según pestaña */}
        {activeTab === 'day' ? (
          renderAgendaGrid()
        ) : activeTab === 'week' ? (
          renderWeekView()
        ) : filteredAppointments.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No hay citas que coincidan con tus filtros</p>
          </div>
        ) : (
          <>
            {/* Vista Escritorio (Tabla) */}
            <div className={styles.desktopTableView}>
              <div className={styles.tableContainer}>
                <table className={styles.appointmentsTable}>
                  <thead>
                    <tr>
                      <th>Turno</th>
                      <th>Cliente</th>
                      <th>Servicio</th>
                      <th>Motivo / Obra Social</th>
                      <th>Costo Total</th>
                      <th>Seña Pagada</th>
                      <th>Cobertura OS</th>
                      <th>Estado</th>
                      <th className={styles.toChargeHeader}>A COBRAR</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((appt) => {
                      const toCharge = parseFloat(appt.total_price || 0) - parseFloat(appt.booking_fee_paid || 0) - parseFloat(appt.coverage_amount || 0);
                      const isExpanded = expandedApptId === appt.id;
                      return (
                        <tr 
                          key={appt.id}
                          className={`${isExpanded ? styles.cardExpanded : ''}`}
                          onClick={() => toggleExpandAppt(appt.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className={styles.dateTime} data-label="Turno">
                            <div className={styles.date}>
                              {(() => {
                                const dateStr = String(appt.appointment_date).split('T')[0];
                                const d = new Date(dateStr + 'T12:00:00');
                                return d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
                              })()}
                            </div>
                            <div className={styles.time}>{appt.appointment_time}</div>
                            <div className={styles.miniCode}>{appt.appointment_code}</div>
                            <div className={`${styles.mobileChevron} ${isExpanded ? styles.rotated : ''}`}>
                              <Icon name="chevronRight" size={16} />
                            </div>
                          </td>
                          <td className={styles.patientInfo} data-label="Cliente">
                            <div className={styles.pName}>{appt.patient_name}</div>
                            <div className={styles.pPhone}>{appt.patient_phone}</div>
                          </td>
                          <td data-label="Servicio">
                            <span className={styles.serviceNameBadge}>
                              {appt.service_name || 'Consulta General'}
                            </span>
                          </td>
                          <td className={styles.reasonCol} data-label="Motivo / OS">
                            <div className={styles.reasonText}>{appt.reason_for_visit || 'Visita general'}</div>
                            {appt.insurance_name && (
                              <div className={styles.insuranceBadge}>
                                {appt.insurance_name}
                                {appt.insurance_plan_name ? ` (${appt.insurance_plan_name})` : ''}
                              </div>
                            )}
                          </td>
                          <td className={styles.amount} data-label="Total">
                            ${parseFloat(appt.total_price || 0).toLocaleString()}
                          </td>
                          <td className={styles.feePaid} data-label="Seña">
                            ${parseFloat(appt.booking_fee_paid || 0).toLocaleString()}
                          </td>
                          <td className={styles.coverage} data-label="Cobertura">
                            {parseFloat(appt.coverage_amount || 0) > 0 ? `-$${parseFloat(appt.coverage_amount).toLocaleString()}` : '-'}
                          </td>
                          <td data-label="Estado">{getStatusBadge(appt.status)}</td>
                          <td className={styles.toChargeCell} data-label="A COBRAR">
                            <div className={styles.toChargeAmount}>
                              ${toCharge > 0 ? toCharge.toLocaleString() : '0'}
                            </div>
                          </td>
                          <td className={styles.actions} data-label="Acciones">
                            <div className={styles.actionButtons}>
                            {['pending', 'pending_payment'].includes(appt.status) ? (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAcceptAppointment(appt.id); }}
                                  className={styles.acceptBtn}
                                  title="Confirmar/Aceptar cita"
                                >
                                  ✓ Aceptar
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRejectAppointment(appt.id); }}
                                  className={styles.rejectBtn}
                                  title="Rechazar cita"
                                >
                                  ✗ Rechazar
                                </button>
                              </>
                            ) : ['scheduled', 'absent'].includes(appt.status) ? (
                              <>
                                <select
                                  value={appt.status}
                                  onChange={(e) => { e.stopPropagation(); handleStatusChange(appt.id, e.target.value); }}
                                  className={styles.statusSelect}
                                >
                                  <option value="scheduled">Programado</option>
                                  <option value="completed">Completado</option>
                                  <option value="cancelled">Cancelado</option>
                                  <option value="absent">Ausente</option>
                                </select>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDelayModal({ show: true, appointmentId: appt.id }); }}
                                  className={styles.delayBtn}
                                  title="Registrar retraso"
                                >
                                  ⏱️ Retraso
                                </button>
                              </>
                            ) : (
                              <div className={styles.statusOnly}>
                                {getStatusBadge(appt.status)}
                              </div>
                            )}
                            
                            {/* WhatsApp siempre disponible si hay teléfono y no es estado final negativo */}
                            {!['cancelled', 'rejected'].includes(appt.status) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleWhatsAppReminder(appt); }}
                                className={styles.whatsappBtn}
                                title="Recordar cita por WhatsApp"
                              >
                                <Icon name="whatsapp" size={18} color="#FFF" />
                                WPP
                              </button>
                            )}
                            </div>
                            {appt.delay_minutes > 0 && (
                              <div className={styles.delayBadge}>+{appt.delay_minutes} min</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Vista Móvil (Tarjetas) */}
            <div className={styles.mobileCardsList}>
              {filteredAppointments.map((appt) => {
                const toCharge = parseFloat(appt.total_price || 0) - parseFloat(appt.booking_fee_paid || 0) - parseFloat(appt.coverage_amount || 0);
                return (
                  <div key={appt.id} className={styles.mobileApptCard}>
                    <div className={styles.cardHeaderMobile}>
                      <div className={styles.mobileApptDateTime}>
                        <strong>
                          {(() => {
                            const dateStr = String(appt.appointment_date).split('T')[0];
                            const d = new Date(dateStr + 'T12:00:00');
                            return d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', weekday: 'short' });
                          })()}
                        </strong>
                        <span className={styles.mobileApptTime}>
                          ⏱️ {appt.appointment_time} hs
                        </span>
                      </div>
                      <div className={styles.mobileApptStatus}>
                        {getStatusBadge(appt.status)}
                      </div>
                    </div>

                    <div className={styles.cardBodyMobile}>
                      <h3 className={styles.mobilePatientName}>{appt.patient_name}</h3>
                      <div className={styles.mobilePatientPhone}>📞 {appt.patient_phone || 'Sin teléfono'}</div>
                      <div className={styles.mobileApptService}>
                        <span className={styles.serviceNameBadge}>
                          {appt.service_name || 'Consulta General'}
                        </span>
                      </div>
                      {appt.reason_for_visit && (
                        <p className={styles.mobileApptReason}>
                          <strong>Motivo:</strong> {appt.reason_for_visit}
                        </p>
                      )}
                      {appt.insurance_name && (
                        <div className={styles.mobileApptInsurance}>
                          🛡️ {appt.insurance_name} {appt.insurance_plan_name ? `(${appt.insurance_plan_name})` : ''}
                        </div>
                      )}
                      
                      <div className={styles.mobilePricesGroup}>
                        <div className={styles.mobilePriceRow}>
                          <span>Costo Total:</span>
                          <strong>${parseFloat(appt.total_price || 0).toLocaleString()}</strong>
                        </div>
                        {parseFloat(appt.booking_fee_paid || 0) > 0 && (
                          <div className={styles.mobilePriceRow}>
                            <span>Seña Pagada:</span>
                            <strong>${parseFloat(appt.booking_fee_paid).toLocaleString()}</strong>
                          </div>
                        )}
                        {parseFloat(appt.coverage_amount || 0) > 0 && (
                          <div className={styles.mobilePriceRow}>
                            <span>Cobertura:</span>
                            <strong>-${parseFloat(appt.coverage_amount).toLocaleString()}</strong>
                          </div>
                        )}
                        <div className={`${styles.mobilePriceRow} ${styles.mobileToChargeRow}`}>
                          <span>A Cobrar:</span>
                          <strong>${toCharge > 0 ? toCharge.toLocaleString() : '0'}</strong>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardActionsMobile}>
                      {['pending', 'pending_payment'].includes(appt.status) ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAcceptAppointment(appt.id); }}
                            className={styles.mobileAcceptBtn}
                          >
                            Aceptar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRejectAppointment(appt.id); }}
                            className={styles.mobileRejectBtn}
                          >
                            Rechazar
                          </button>
                        </>
                      ) : ['scheduled', 'absent'].includes(appt.status) ? (
                        <>
                          <select
                            value={appt.status}
                            onChange={(e) => { e.stopPropagation(); handleStatusChange(appt.id, e.target.value); }}
                            className={styles.mobileStatusSelect}
                          >
                            <option value="scheduled">Programado</option>
                            <option value="completed">Completado</option>
                            <option value="cancelled">Cancelado</option>
                            <option value="absent">Ausente</option>
                          </select>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDelayModal({ show: true, appointmentId: appt.id }); }}
                            className={styles.mobileDelayBtn}
                          >
                            ⏱️ Retrasar
                          </button>
                        </>
                      ) : null}

                      {!['cancelled', 'rejected'].includes(appt.status) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleWhatsAppReminder(appt); }}
                          className={styles.mobileWhatsappBtn}
                        >
                          <Icon name="whatsapp" size={18} color="#FFF" />
                          <span>WhatsApp</span>
                        </button>
                      )}
                    </div>

                    {appt.delay_minutes > 0 && (
                      <div className={styles.mobileDelayBadge}>
                        ⏱️ Retrasado +{appt.delay_minutes} min
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className={styles.footer}>
          <p>Total: <strong>{filteredAppointments.length}</strong> cita(s)</p>
        </div>

        {/* Delay Modal */}
        {delayModal.show && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h2>Registrar Retraso</h2>
                <button
                  onClick={() => {
                    setDelayModal({ show: false, appointmentId: null });
                    setDelayMinutes(15);
                  }}
                  className={styles.closeBtn}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: '1.5rem' }}>
                <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                  ¿Cuántos minutos de retraso tiene esta cita?
                </p>

                <div className={styles.delayOptions}>
                  {[15, 30, 45, 60].map(mins => (
                    <button
                      key={mins}
                      onClick={() => setDelayMinutes(mins)}
                      className={`${styles.optionBtn} ${delayMinutes === mins ? styles.active : ''}`}
                    >
                      {mins} min
                    </button>
                  ))}
                </div>

                <div className={styles.customInput}>
                  <label>O ingresa minutos personalizados:</label>
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={delayMinutes}
                    onChange={(e) => setDelayMinutes(parseInt(e.target.value) || 0)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.modalButtons}>
                  <button
                    onClick={() => {
                      setDelayModal({ show: false, appointmentId: null });
                      setDelayMinutes(15);
                    }}
                    className={styles.cancelBtn}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDelay}
                    className={styles.confirmBtn}
                  >
                    Registrar Retraso
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DoctorLayout>
  );
}
